import { appPath } from '@featit/shared/addressing/paths';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import { useStores } from '@/hooks/useStores';
import { useT } from '@/i18n/LocaleProvider';
import { useFormat } from '@/i18n/useFormat';
import { TOUCH_TARGET_SQUARE } from './touchTarget';

/** Settings → Invitations: where the badge, the notice and the profile's entry all lead (I-2233). */
export const INVITATIONS_SETTINGS_SECTION = 'invitations';
export const INVITATIONS_SETTINGS_PATH = appPath(
  `/settings?section=${INVITATIONS_SETTINGS_SECTION}`,
);

/**
 * I-2233 (`docs/concept-invitations-to-several-work-areas.md` AC-PI.2): the count on the profile line
 * while invitations wait for the person. Jay, 2026-10-27: "if nothing is there yet show a
 * notification on the profile line".
 *
 * A link of its own beside the profile's, to Settings → Invitations, so the count goes where it is
 * about while the name still opens the profile. Its accessible name says whose line it is and how
 * many wait ("Jay, 2 invitations waiting"). Zero draws nothing, as the sidebar's other counts do,
 * and so does a read that is still in flight or failed: the line never claims a number it lacks.
 */
export const InvitationsWaitingBadge = observer(function InvitationsWaitingBadge({
  name,
  onNavigate,
  className = '',
}: {
  /** The signed-in person's name, which the accessible name starts with. */
  name: string;
  onNavigate?: () => void;
  className?: string;
}) {
  const t = useT();
  const format = useFormat();
  const count = useStores()?.invitationsWaitingStore?.count;
  if (!count) return null;
  return (
    <Link
      to={INVITATIONS_SETTINGS_PATH}
      onClick={onNavigate}
      data-testid="invitations-waiting-badge"
      // F-0579 ACC-5: a count's number takes the highlight in Colour.
      data-accent-count=""
      aria-label={t('navigation.sidebar.invitationsWaiting', { name, count })}
      title={t('navigation.sidebar.invitationsWaitingTitle', { count })}
      // The link is the 44 px target wherever a finger points; the pill inside stays small.
      className={`group grid shrink-0 place-items-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-focus ${TOUCH_TARGET_SQUARE} ${className}`}
    >
      {/* The bell's own badge colours: a count that asks for the person reads as one. */}
      <span className="grid h-[18px] min-w-[18px] place-items-center rounded-full bg-[var(--evx-badge)] px-1.5 text-[11px] font-bold leading-none tabular-nums text-[var(--evx-on-badge)] transition-transform group-hover:scale-110 motion-reduce:transition-none">
        {format.number(count)}
      </span>
    </Link>
  );
});
